import { useMemo } from 'react';

import { useTopicDetail, usePractice } from './useLms';
import { useProblems } from './useProblems';

/** One problem in whatever ordered set the student is working through. */
export interface SequenceEntry {
  slug: string;
  title: string;
  solved?: boolean;
}

export interface ProblemSequence {
  /** Human name for the set — "Week 4 · Pointers". Absent for the catalogue. */
  label?: string;
  entries: SequenceEntry[];
  /** Position of the current problem, or -1 when it is not in this set. */
  index: number;
  prev: SequenceEntry | null;
  next: SequenceEntry | null;
  /** How many of the set are solved. Only meaningful when `label` is set —
   *  the global catalogue does not report solved state through this path. */
  solvedCount: number;
  /** True for a real, named set. False for the global-catalogue fallback,
   *  where "3 of 2539" would be noise rather than progress. */
  isScoped: boolean;
}

/**
 * Resolves what "next problem" means for the student who is looking at it.
 *
 * Prev/next used to index into `useProblems({ limit: 100 })` — the flat global
 * catalogue. That ignored where the student came from and went dead at problem
 * 100 of 2539. A student working Week 4 got "next" = whatever happened to be
 * alphabetically adjacent in the whole platform.
 *
 * Precedence, most specific first:
 *   1. `topicId`  — the topic's assessments, in authored order, with solved marks
 *   2. `batchId`  — the practice set (`group_key`) this problem belongs to
 *   3. neither    — the global catalogue, exactly as before
 *
 * The fallback is load-bearing: a cold `/problems/two-sum` from search or the
 * command palette has no origin, and must keep working the way it does today.
 *
 * Both scoped sources are already fetched elsewhere on these journeys, so in
 * practice this reads from the react-query cache rather than adding a request.
 */
export function useProblemSequence(
  slug: string,
  origin: { topicId?: string; batchId?: string },
): ProblemSequence {
  const { topicId, batchId } = origin;

  // Only the more specific source is fetched: asking for a whole batch's
  // practice overview when we already have a topic would be a wasted request.
  const { data: topic } = useTopicDetail(topicId ?? '');
  const { data: practice } = usePractice(!topicId && batchId ? batchId : '');

  const scoped = useMemo(
    () => topicSequence(topic, slug) ?? practiceSequence(practice, slug),
    [topic, practice, slug],
  );

  // The catalogue is the fallback, and it has to be a real one. Gating it on
  // "no origin was passed" was wrong: a student who arrives with a topicId for
  // a problem that is not in that topic would get an empty sequence and dead
  // prev/next — strictly worse than the behaviour this replaced. Gate on
  // whether a scoped set actually resolved instead, so the catalogue loads
  // whenever nothing better exists. Limit stays at 100 to preserve today's
  // behaviour rather than quietly change it.
  const { data: catalogue } = useProblems({ limit: 100 }, { enabled: !scoped });

  return useMemo(() => {
    if (scoped) return scoped;

    const entries: SequenceEntry[] = (catalogue?.problems ?? []).map((p) => ({
      slug: p.slug,
      title: p.title,
    }));
    return build(undefined, entries, slug, false);
  }, [scoped, catalogue, slug]);
}

/**
 * A topic's coding problems, in the order they were authored.
 *
 * Assessments without a `problem_slug` (quizzes, assignments, the viva) are
 * dropped: they are not reachable at /problems/$slug, so including them would
 * make "next" navigate to a page that cannot render them.
 *
 * Returns null rather than an empty sequence when the current problem is not a
 * member — a student who followed a link out of the topic should fall through
 * to a set that actually contains where they are, not get a dead prev/next.
 */
function topicSequence(
  topic: { title?: string; module_title?: string; assessments?: { problem_slug?: string; title: string; solved?: boolean }[] } | undefined,
  slug: string,
): ProblemSequence | null {
  if (!topic?.assessments) return null;
  const entries = topic.assessments
    .filter((a): a is { problem_slug: string; title: string; solved?: boolean } => Boolean(a.problem_slug))
    .map((a) => ({ slug: a.problem_slug, title: a.title, solved: a.solved }));
  if (!entries.some((e) => e.slug === slug)) return null;

  const label = topic.module_title ? `${topic.module_title} · ${topic.title}` : topic.title;
  return build(label, entries, slug, true);
}

/**
 * The authored practice set this problem belongs to — a week's ten problems on
 * the warm-up / core / stretch / debug ladder.
 *
 * Scoped to the problem's own `group_key` rather than the whole batch: a batch
 * holds every problem of every week, and "next" across all of them is barely
 * better than the global catalogue it replaced. Items with no `group_key` (the
 * legacy assessments that stay loose) cannot form a set, so they fall through.
 */
function practiceSequence(
  practice: { items?: { problem_slug?: string; title: string; group_key?: string; group_label?: string; submitted?: boolean }[] } | undefined,
  slug: string,
): ProblemSequence | null {
  const items = practice?.items;
  if (!items) return null;

  const current = items.find((i) => i.problem_slug === slug);
  if (!current?.group_key) return null;

  const entries = items
    .filter((i) => i.group_key === current.group_key && i.problem_slug)
    .map((i) => ({ slug: i.problem_slug as string, title: i.title, solved: i.submitted }));
  if (entries.length < 2) return null;

  return build(current.group_label ?? 'Practice set', entries, slug, true);
}

function build(
  label: string | undefined,
  entries: SequenceEntry[],
  slug: string,
  isScoped: boolean,
): ProblemSequence {
  const index = entries.findIndex((e) => e.slug === slug);
  return {
    label,
    entries,
    index,
    prev: index > 0 ? entries[index - 1] : null,
    next: index >= 0 && index < entries.length - 1 ? entries[index + 1] : null,
    solvedCount: entries.filter((e) => e.solved).length,
    isScoped,
  };
}
